import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,

  Typography
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { useDispatch, useSelector } from 'react-redux';
import AnimatedNumbers from 'react-animated-numbers';
import DynamicSearchMenu from '../../../Booking/Components/searchOptionComponents/DynamicSearchMenu';
import {
  initialBookingForm,
  searchDestination,
  searchPortLoading,
  searchShippingLine
} from '../../../../actions/formAction';
import MixSearch from '../../../Booking/Components/searchOptionComponents/MixSearch';
import BookingTable from '../../../Booking/Components/Tabel/BookingTable';
import { getCount } from '../../../../actions/getCountAction';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { getMangeData } from '../../../../actions/mangeAction';
import AdvancedSearch from '../../../Vehicles/Components/searchOptionComponents/MultiFieldSearch ';
import Progress from '../../../../components/Loader/Progress ';
import PropTypes from "prop-types";

/* ---------- Constants ---------- */
const COLOR_PALETTE = ['#0B3C5D', '#D9B310', '#1A5F8C', '#A98A0C', '#F3D351', '#051E30'];
const DARK_INDEXES = [0, 2, 5];

function AdvancedSearchBookingModal({
  open,
  onClose,
  onSelect,
  autoFetchOnOpen = true,
  bookingId = null
}) {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const formReducer = useSelector((s) => s.verificationBooking);
  const countReducer = useSelector((s) => s.fetchCount);
  const fetchData = useSelector((s) => s.manage);

  const [advancedSearchFields, setAdvancedSearchFields] = useState(
    formReducer?.data?.advancedSearches || []
  );

  /* ---------- Helpers ---------- */
  const buildQueryString = React.useCallback(() => {
    const sp = new URLSearchParams(searchParams.toString());
    if (!sp.get('SearchOption')) sp.set('SearchOption', '10');
    if (bookingId == null || bookingId === '') {
      sp.delete('BookingId');
    } else {
      sp.set('BookingId', bookingId);
    }
    return sp.toString();
  }, [searchParams, bookingId]);

  const handleSelectBooking = (booking) => {
    if (onSelect) onSelect(booking);
    onClose();
  };

  /* ---------- Effects ---------- */
  // Initial fetch when opening dialog.
  useEffect(() => {
    if (!open) return;
    dispatch(initialBookingForm());
    dispatch(getCount({ mode: 'booking' }));
    if (autoFetchOnOpen) {
      dispatch(getMangeData({ mode: 'booking', query: buildQueryString() }));
    }
    return () => {
      if (open) resetFilters();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Refetch when search params (paging/filtering) or bookingId change while open.
  useEffect(() => {
    if (!open) return;
    dispatch(getMangeData({ mode: 'booking', query: buildQueryString() }));
  }, [open, searchParams, bookingId, buildQueryString, dispatch]);

  // Keep local advanced search fields updated if reducer changes.
  useEffect(() => {
    if (formReducer?.data?.advancedSearches) {
      setAdvancedSearchFields(formReducer.data.advancedSearches);
    }
  }, [formReducer.data?.advancedSearches]);

  /* ---------- Handlers ---------- */
  const handleSearch = () => {
    if (!open) return;
    dispatch(getMangeData({ mode: 'booking', query: buildQueryString() }));
  };

  const resetFilters = () => {
    const params = new URLSearchParams(location.search);
    [
      'ShippingLine',
      'PortLoading',
      'Destination',
      'SearchOption',
      'Status',
      'AdvancedSearch',
      'MixSearch',
      'PageSize',
      'PageNumber',
      'SortOrder',
      'SortBy'
    ].forEach(k => params.delete(k));
    navigate({ search: params.toString() });
  };

  const handleCardClick = (type, id) => {
   

     const searchParams = new URLSearchParams(location.search);

        // Set the 'name' parameter to the given id
        if (type === 'Status') {
            searchParams.set('Status', id);
            searchParams.delete('SearchOption');


        } else {
          searchParams.set("SearchOption",`${id}`)
          searchParams.delete('Status');


        }

          navigate({ search: searchParams.toString() });

  };

  const isCountsLoading = countReducer.loading === 'pending' || countReducer.loading === 'idle';
  const isTableLoading = fetchData.loading === 'pending' ;

  let stats = useMemo(() => countReducer.Object || [], [countReducer.Object]);


  stats = stats.filter(item => item.name === "BOOKED" || item.name === "DOC CUTOFF (Next 4 Days)" || item.name === "RECEIVING (Next 10 Days)" || item.name === "RECEIVING OPEN" );

  /* ---------- Render Helpers ---------- */
  const renderStats = () => {
    if (isCountsLoading) return <Progress />;
    return (
      <Box
        sx={{
          display: 'grid',
            gridTemplateColumns: {
              xs: 'repeat(auto-fill, minmax(120px, 1fr))',
              sm: 'repeat(auto-fill, minmax(130px, 1fr))',
              md: 'repeat(auto-fill, minmax(140px, 1fr))',
              lg: 'repeat(auto-fill, minmax(140px, 1fr))',
            },
          gap: { xs: 1.5, sm: 2, md: 2.5 },
          mb: { xs: 2, md: 3 }
        }}
      >
        {stats.map((item, index) => {
          const circleColor = COLOR_PALETTE[index % COLOR_PALETTE.length];
          const textColor = DARK_INDEXES.includes(index % COLOR_PALETTE.length) ? '#fff' : '#0B3C5D';
          return (
            <Box
              key={index}
              onClick={() => handleCardClick(item.type, item.id)}
              sx={{
                position: 'relative',
                borderRadius: 2,
                bgcolor: '#fff',
                boxShadow: '0 1px 4px rgba(11,60,93,0.15)',
                border: '1px solid #e2e8f0',
                p: 1.2,
                minHeight: 125,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                cursor: 'pointer',
                overflow: 'hidden',
                transition: 'transform .18s, box-shadow .18s, border-color .18s',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: '0 6px 16px rgba(11,60,93,0.20)',
                  borderColor: '#c9d6df'
                },
                '&:active': { transform: 'translateY(-2px) scale(.985)' }
              }}
            >
              <Box
                sx={{
                  width: { xs: 50, sm: 54, md: 56 },
                  height: { xs: 50, sm: 54, md: 56 },
                  borderRadius: '50%',
                  background: `linear-gradient(140deg, ${circleColor} 55%, #ffffff 120%)`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mb: 1,
                  boxShadow: '0 3px 10px -3px rgba(11,60,93,0.35)'
                }}
              >
                <Typography
                  sx={{
                    fontSize: { xs: 20, sm: 21, md: 22 },
                    fontWeight: 800,
                    color: textColor,
                    lineHeight: 1,
                    textShadow: textColor === '#fff' ? '0 1px 3px #0B3C5D55' : 'none'
                  }}
                >
                  <AnimatedNumbers
                    animateToNumber={item.count}
                    fontStyle={{ fontSize: 'inherit' }}
                    configs={(n, i) => ({ mass: 1, tension: 200 + (i + 1) * 25, friction: 42 })}
                  />
                </Typography>
              </Box>
              <Typography
                sx={{
                  fontWeight: 600,
                  fontSize: { xs: 11.5, sm: 12.5 },
                  color: '#0B3C5D',
                  textAlign: 'center',
                  lineHeight: 1.2,
                  px: 0.5,
                  wordBreak: 'break-word',
                  mb: 0.5
                }}
              >
                {item.name}
              </Typography>
              <Box
                sx={{
                  position: 'absolute',
                  left: 0,
                  bottom: 0,
                  height: 3,
                  width: '100%',
                  background: `linear-gradient(90deg, ${circleColor} 0%, rgba(11,60,93,0.4) 60%, transparent 100%)`,
                  opacity: 0.85
                }}
              />
            </Box>
          );
        })}
      </Box>
    );
  };

  const renderFilters = () => (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: {
          xs: 'repeat(auto-fill,minmax(140px,1fr))',
          sm: 'repeat(auto-fill,minmax(160px,1fr))',
          md: 'repeat(auto-fill,minmax(180px,1fr))'
        },
        gap: { xs: 1.5, sm: 2 },
        mb: { xs: 2, md: 2.5 }
      }}
    >
      <DynamicSearchMenu
        label="Destination"
        queryParam="Destination"
        loadingKey="isLoadingDestinationData"
        dataKey="destination"
        fetchAction={searchDestination}
      />
      <DynamicSearchMenu
        label="Port Loading"
        queryParam="PortLoading"
        loadingKey="isLoadingPortData"
        dataKey="portLoading"
        fetchAction={searchPortLoading}
      />
      <DynamicSearchMenu
        label="Shipping Line"
        queryParam="ShippingLine"
        loadingKey="isLoadingShippingLineData"
        dataKey="shippingLine"
        fetchAction={searchShippingLine}
      />
      {formReducer.loading === 'pending' || formReducer.loading === 'idle'
        ? <Progress />
        : <AdvancedSearch filter={advancedSearchFields} />
      }
    </Box>
  );

  const renderSearchBar = () => (
    <Box
      sx={{
        display: 'flex',
        flexDirection: { xs: 'column', sm: 'row' },
        gap: 1.5,
        mb: { xs: 2, md: 2.5 },
        alignItems: { xs: 'stretch', sm: 'center' },
        justifyContent: 'space-between'
      }}
    >
      <MixSearch sx={{ minWidth: { xs: '100%', sm: 220 } }} />
      <Box
        sx={{
          display: 'flex',
          gap: 1,
          flexWrap: 'wrap',
          justifyContent: { xs: 'flex-start', sm: 'flex-end' }
        }}
      >
        <Button
          variant="contained"
          size="small"
          startIcon={<SearchIcon />}
          onClick={handleSearch}
          sx={{ minWidth: 90 }}
        >
          Search
        </Button>
        <Button
          variant="outlined"
          size="small"
          onClick={resetFilters}
          sx={{ minWidth: 80 }}
        >
          Reset
        </Button>
      </Box>
    </Box>
  );

  const renderTable = () => (
    <Box
      sx={{
        position: 'relative',
        minHeight: { xs: 160, sm: 200 },
        border: '1px solid #e2e8f0',
        borderRadius: 2,
        overflow: 'hidden'
      }}
    >
      {isTableLoading ? (
        <Progress Msg="Loading ..." />
      ) : (
        <BookingTable showCreateShipment={false} handleSelectBooking={handleSelectBooking} />
      )}
    </Box>
  );

  /* ---------- Render ---------- */
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg">
      <DialogTitle sx={{ fontWeight: 600 }}>
        Advanced Booking Search
      </DialogTitle>
      <DialogContent dividers sx={{ pt: 2 }}>
        {renderStats()}
        {renderFilters()}
        {renderSearchBar()}
        {renderTable()}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="text">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
 
}
 AdvancedSearchBookingModal.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    onSelect: PropTypes.func,
  };
export default AdvancedSearchBookingModal;